import type { CartRepository } from '../../domain/repositories/CartRepository';
import { CartItem } from '../../domain/entities/CartItem';
import type { CartItemDTO, CartResponseDTO } from '../../application/dtos/CartDTO';
import { httpService } from '@/services/HttpService';

export class HttpCartRepository implements CartRepository {
  private readonly baseUrl = '/cart';

  async getCart() {
    const response = await httpService.get<CartResponseDTO>(this.baseUrl);
    return this.mapResponse(response);
  }

  async addItem(entityType: string, entityId: string, quantity: number) {
    const response = await httpService.post<CartResponseDTO>(`${this.baseUrl}/items`, {
      product_entity_type: entityType,
      product_entity_id: entityId,
      quantity,
    });
    return this.mapResponse(response);
  }

  async updateQuantity(itemId: string, quantity: number) {
    const response = await httpService.put<CartResponseDTO>(`${this.baseUrl}/items/${itemId}`, {
      quantity,
    });
    return this.mapResponse(response);
  }

  async removeItem(itemId: string) {
    const response = await httpService.delete<CartResponseDTO>(`${this.baseUrl}/items/${itemId}`);
    return this.mapResponse(response);
  }

  async clearCart(): Promise<void> {
    await httpService.delete(this.baseUrl);
  }

  private mapResponse(response: CartResponseDTO) {
    return {
      items: response.data.items.map(dto => this.mapToEntity(dto)),
      subtotal: this.toNumber(response.data.summary.subtotal),
      total: this.toNumber(response.data.summary.total),
    };
  }

  private mapToEntity(dto: CartItemDTO): CartItem {
    const productSource = (dto.product_info ?? dto.product ?? {
      name: 'Product',
      image: null,
      is_digital: undefined,
    }) as { name: string; image: string | null; is_digital?: boolean };

    const isDigital = productSource.is_digital ?? (dto.product_entity_type === 'gallery');

    return new CartItem(
      dto.id,
      dto.product_entity_type,
      dto.product_entity_id,
      dto.quantity,
      {
        name: productSource.name,
        price: this.toNumber(dto.unit_price),
        image: productSource.image ?? null,
        isDigital: isDigital,
      },
      dto.is_purchable ?? true,
      this.toNumber(dto.subtotal)
    );
  }

  private toNumber(value: number | string): number {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
